import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { MinusIcon, PlusIcon } from '@shared/assets/icons';
import { Text } from '@shared/ui/atoms';
import { accordionSizes, accordionItemTitlePaddingsRem, accordionItemTitlePaddingsPx, accordionItemIconWrapperSizesRem, accordionItemIconWrapperSizesPx, accordionItemIconSizesRem, accordionItemIconSizesPx, accordionItemContentMarginBottomOpenPx, accordionItemTitleTextSizesDesktop, accordionItemTitleTextSizesMobile, accordionItemContentTextSizesDesktop, accordionItemContentTextSizesMobile, accordionItemContentMarginBottomOpenRem } from '@shared/config/components/accordion';
import { withTheme } from '@shared/lib/theme';
type SizeValue = keyof typeof accordionSizes;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
export type AccordionItemType = {
  title: ReactNode;
  content: ReactNode;
};
interface AccordionItemProps {
  size: ResponsiveSize;
  index: number;
  item: AccordionItemType;
  isOpen: boolean;
  toggleAccordionItemState: (index: number) => void;
}
const AccordionItem: FC<AccordionItemProps> = ({
  size = 'l',
  index,
  item,
  isOpen,
  toggleAccordionItemState
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (ref.current) {
      setHeight(isOpen ? ref.current.scrollHeight : 0);
    }
  }, [isOpen, item.content]);
  const desktopSize = typeof size === 'object' ? size.desktop || 'l' : size || 'l';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || 'l';
  return <ItemWrapper key={index}>
      <TitleWrapper type="button" desktopSize={desktopSize} mobileSize={mobileSize} open={isOpen} onClick={() => toggleAccordionItemState(index)}>
        <Text size={{
        desktop: accordionItemTitleTextSizesDesktop[desktopSize],
        mobile: accordionItemTitleTextSizesMobile[mobileSize]
      }} family="MTSCompact">
          {item.title}
        </Text>
        <IconWrapper desktopSize={desktopSize} mobileSize={mobileSize} open={isOpen}>
          {isOpen ? <MinusIcon /> : <PlusIcon />}
        </IconWrapper>
      </TitleWrapper>
      <ContentWrapper ref={ref} desktopSize={desktopSize} mobileSize={mobileSize} open={isOpen} style={{
      height: `${height}px`
    }}>
        <Text as="div" size={{
        desktop: accordionItemContentTextSizesDesktop[desktopSize],
        mobile: accordionItemContentTextSizesMobile[mobileSize]
      }} color="secondary" family="MTSCompact">
          {item.content}
        </Text>
      </ContentWrapper>
    </ItemWrapper>;
};
export default AccordionItem;
const ItemWrapper = /*#__PURE__*/styled('div')({
  name: "ItemWrapper",
  class: "ib8ij7u",
  propsAsIs: false
});
type TitleWrapperProps = {
  open: boolean;
} & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp = /*#__PURE__*/() => ({
  desktopSize
}) => accordionItemTitlePaddingsRem[desktopSize];
const _exp2 = /*#__PURE__*/() => ({
  mobileSize
}) => accordionItemTitlePaddingsPx[mobileSize];
const TitleWrapper = /*#__PURE__*/styled('button')({
  name: "TitleWrapper",
  class: "t13ohamd",
  propsAsIs: false,
  vars: {
    "t13ohamd-0": [_exp()],
    "t13ohamd-1": [_exp2()]
  }
});
type IconWrapperProps = {
  open: boolean;
} & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp3 = /*#__PURE__*/() => ({
  open
}) => open ? '180deg' : '0deg';
const _exp5 = /*#__PURE__*/() => ({
  desktopSize
}) => accordionItemIconWrapperSizesRem[desktopSize];
const _exp6 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.tertiary;
const _exp7 = /*#__PURE__*/() => ({
  desktopSize
}) => accordionItemIconSizesRem[desktopSize].width;
const _exp8 = /*#__PURE__*/() => ({
  desktopSize
}) => accordionItemIconSizesRem[desktopSize].height;
const _exp9 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.icon.primary;
const _exp1 = /*#__PURE__*/() => ({
  mobileSize
}) => accordionItemIconWrapperSizesPx[mobileSize];
const _exp10 = /*#__PURE__*/() => ({
  mobileSize
}) => accordionItemIconSizesPx[mobileSize].width;
const _exp11 = /*#__PURE__*/() => ({
  mobileSize
}) => accordionItemIconSizesPx[mobileSize].height;
const IconWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "iis79ok",
  propsAsIs: false,
  vars: {
    "iis79ok-0": [_exp3()],
    "iis79ok-1": [_exp5()],
    "iis79ok-2": [_exp6()],
    "iis79ok-3": [_exp7()],
    "iis79ok-4": [_exp8()],
    "iis79ok-5": [_exp9()],
    "iis79ok-6": [_exp1()],
    "iis79ok-7": [_exp10()],
    "iis79ok-8": [_exp11()]
  }
}));
type ContentWrapperProps = {
  open: boolean;
} & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp12 = /*#__PURE__*/() => ({
  open,
  desktopSize
}) => open ? accordionItemContentMarginBottomOpenRem[desktopSize] : '0';
const _exp13 = /*#__PURE__*/() => ({
  open,
  mobileSize
}) => open ? accordionItemContentMarginBottomOpenPx[mobileSize] : '0';
const ContentWrapper = /*#__PURE__*/styled('div')({
  name: "ContentWrapper",
  class: "c1gpi302",
  propsAsIs: false,
  vars: {
    "c1gpi302-0": [_exp12()],
    "c1gpi302-1": [_exp13()]
  }
});

require("./AccordionItem.linaria.module.css!=!../../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./AccordionItem.tsx");